import { markRaw } from 'vue'
import ComplianceStatusColumn from './ComplianceStatusColumn.vue'
import ComplianceUpdatedByColumn from './ComplianceUpdatedByColumn.vue'
import ComplianceNextReviewColumn from './ComplianceNextReviewColumn.vue'
import RecruiterComplianceInvitedOnColumn from './RecruiterComplianceInvitedOnColumn.vue'
import RecruiterComplianceStatusColumn from './RecruiterComplianceStatusColumn.vue'
import RecruiterComplianceUpdatedByColumn from './RecruiterComplianceUpdatedByColumn.vue'
import {
  complianceDate,
  complianceReviewerName,
  complianceStatus,
  reviewStatusText,
  VerifiedComplianceDateKey,
} from './verifiedComplianceData'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import type { ColumnMaps } from '@/components/GraphqlDataTable/columns/types'
import { type FragmentType, useFragment } from '@/gql'
import {
  ComplianceStatusColumnFragmentDoc,
  ComplianceUpdatedByColumnFragmentDoc,
  ComplianceNextReviewColumnFragmentDoc,
  RecruiterComplianceInvitedOnColumnFragmentDoc,
  RecruiterComplianceStatusColumnFragmentDoc,
  RecruiterComplianceUpdatedByColumnFragmentDoc,
  RecruiterOrderByColumn,
  SortOrder,
} from '@/gql/graphql'

const workerData = (row: FragmentType<typeof ComplianceUpdatedByColumnFragmentDoc>) =>
  useFragment(ComplianceUpdatedByColumnFragmentDoc, row).verifiedCompliance?.[0]?.data

const recruiterData = (row: FragmentType<typeof RecruiterComplianceUpdatedByColumnFragmentDoc>) =>
  useFragment(RecruiterComplianceUpdatedByColumnFragmentDoc, row).verifiedCompliance?.[0]?.data

const ComplianceColumns: ColumnMaps = {
  ComplianceStatusColumn: {
    fragment: ComplianceStatusColumnFragmentDoc,
    component: markRaw(ComplianceStatusColumn),
    columnDef: {
      title: $t('Status'),
      key: 'ComplianceStatusColumn',
      orderAble: false,
      renderUsing: {
        component: markRaw(ComplianceStatusColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: $t('Status'),
        extraction: (row: FragmentType<typeof ComplianceStatusColumnFragmentDoc>) => {
          const data = useFragment(ComplianceStatusColumnFragmentDoc, row).verifiedCompliance?.[0]
            ?.data
          return reviewStatusText(complianceStatus(data))
        },
      },
    ],
  },
  ComplianceUpdatedByColumn: {
    fragment: ComplianceUpdatedByColumnFragmentDoc,
    component: markRaw(ComplianceUpdatedByColumn),
    columnDef: {
      title: $t('Last reviewed'),
      key: 'ComplianceUpdatedByColumn',
      orderAble: false,
      renderUsing: {
        component: markRaw(ComplianceUpdatedByColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: $t('Reviewed by'),
        extraction: (row) => complianceReviewerName(workerData(row)) ?? '',
      },
      {
        title: $t('Reviewed at'),
        extraction: (row) =>
          complianceDate(workerData(row), VerifiedComplianceDateKey.ReviewedAt) ?? '',
      },
    ],
  },
  ComplianceNextReviewColumn: {
    fragment: ComplianceNextReviewColumnFragmentDoc,
    component: markRaw(ComplianceNextReviewColumn),
    columnDef: {
      title: $t('Next review'),
      key: 'ComplianceNextReviewColumn',
      orderAble: false,
      renderUsing: {
        component: markRaw(ComplianceNextReviewColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: $t('Next review'),
        extraction: (row: FragmentType<typeof ComplianceNextReviewColumnFragmentDoc>) => {
          const data = useFragment(ComplianceNextReviewColumnFragmentDoc, row)
            .verifiedCompliance?.[0]?.data
          return complianceDate(data, VerifiedComplianceDateKey.NextReviewAt) ?? ''
        },
      },
    ],
  },
  RecruiterComplianceInvitedOnColumn: {
    fragment: RecruiterComplianceInvitedOnColumnFragmentDoc,
    component: markRaw(RecruiterComplianceInvitedOnColumn),
    columnDef: {
      title: $t('Invited on'),
      key: 'RecruiterComplianceInvitedOnColumn',
      orderAble: true,
      colClass: { 'tw-whitespace-nowrap': true },
      renderUsing: {
        component: markRaw(RecruiterComplianceInvitedOnColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: $t('Invited on'),
        extraction: (row: FragmentType<typeof RecruiterComplianceInvitedOnColumnFragmentDoc>) =>
          useFragment(RecruiterComplianceInvitedOnColumnFragmentDoc, row).invitedAt ?? '',
      },
    ],
    sort: [
      {
        name: `${RecruiterOrderByColumn.RecruiterInvitedAt}_${SortOrder.Desc}`,
        primaryText: $t('Invited on'),
        secondaryText: $t('Most recent first'),
        orderByColumn: RecruiterOrderByColumn.RecruiterInvitedAt,
        sortOrder: SortOrder.Desc,
      },
      {
        name: `${RecruiterOrderByColumn.RecruiterInvitedAt}_${SortOrder.Asc}`,
        primaryText: $t('Invited on'),
        secondaryText: $t('Oldest first'),
        orderByColumn: RecruiterOrderByColumn.RecruiterInvitedAt,
        sortOrder: SortOrder.Asc,
      },
    ],
  },
  RecruiterComplianceStatusColumn: {
    fragment: RecruiterComplianceStatusColumnFragmentDoc,
    component: markRaw(RecruiterComplianceStatusColumn),
    columnDef: {
      title: $t('Status'),
      key: 'RecruiterComplianceStatusColumn',
      orderAble: false,
      renderUsing: {
        component: markRaw(RecruiterComplianceStatusColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: $t('Status'),
        extraction: (row: FragmentType<typeof RecruiterComplianceStatusColumnFragmentDoc>) => {
          const data = useFragment(RecruiterComplianceStatusColumnFragmentDoc, row)
            .verifiedCompliance?.[0]?.data
          return reviewStatusText(complianceStatus(data))
        },
      },
    ],
  },
  RecruiterComplianceUpdatedByColumn: {
    fragment: RecruiterComplianceUpdatedByColumnFragmentDoc,
    component: markRaw(RecruiterComplianceUpdatedByColumn),
    columnDef: {
      title: $t('Last reviewed'),
      key: 'RecruiterComplianceUpdatedByColumn',
      orderAble: true,
      renderUsing: {
        component: markRaw(RecruiterComplianceUpdatedByColumn),
        componentProps: (row, index) => ({ row, index }),
      },
    },
    dataDescriber: [
      {
        title: $t('Reviewed by'),
        extraction: (row) => complianceReviewerName(recruiterData(row)) ?? '',
      },
      {
        title: $t('Reviewed at'),
        extraction: (row) =>
          complianceDate(recruiterData(row), VerifiedComplianceDateKey.ReviewedAt) ?? '',
      },
    ],
    sort: [
      {
        name: `${RecruiterOrderByColumn.ComplianceUpdatedAt}_${SortOrder.Desc}`,
        primaryText: $t('Last reviewed'),
        secondaryText: $t('Most recent first'),
        orderByColumn: RecruiterOrderByColumn.ComplianceUpdatedAt,
        sortOrder: SortOrder.Desc,
      },
      {
        name: `${RecruiterOrderByColumn.ComplianceUpdatedAt}_${SortOrder.Asc}`,
        primaryText: $t('Last reviewed'),
        secondaryText: $t('Oldest first'),
        orderByColumn: RecruiterOrderByColumn.ComplianceUpdatedAt,
        sortOrder: SortOrder.Asc,
      },
    ],
  },
}

export default ComplianceColumns
